import type { FC, ReactNode } from 'react'

import { ArrowUpRight } from 'lucide-react'
import { AvelineLogo } from '@/components/generals/aveline-logo'

const FOOTER_COLUMNS = [
    {
        title: "The House",
        links: [
            { label: "Our story", href: '#house' },
            { label: "Savoir-faire", href: '#savoir-faire' },
            { label: "The Aveline codes", href: '#codes' },
            { label: "Member voices", href: '#voices' }
        ]
    },
    {
        title: "Client services",
        links: [
            { label: "The collection", href: '#collection' },
            { label: "Private reservations", href: '#collection' },
            { label: "Lifetime care", href: '#codes' },
            { label: "Le Cercle", href: '#cercle' }
        ]
    }
]

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='relative isolate overflow-hidden bg-ink text-ivory'>
            <div aria-hidden='true' className='ambient-orb -left-32 -top-40 size-[30rem] bg-gold/15' />

            <div className='mx-auto max-w-7xl px-5 pb-10 pt-20 sm:px-8 sm:pt-24'>
                <div className='grid gap-14 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]'>
                    <div className='max-w-sm'>
                        <AvelineLogo className='text-ivory' markClassName='text-gold' />

                        <p className='mt-6 text-sm leading-relaxed text-ivory/60'>
                            Handbags, fine jewelry and gloves, made from beginning to end by a single pair of hands in our Paris ateliers since 1908.
                        </p>

                        <a
                            href='#cercle'
                            className='group mt-8 inline-flex items-center gap-2 text-[0.78rem] uppercase tracking-[0.2em] text-champagne transition-colors hover:text-ivory focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-champagne'
                        >
                            Apply to Le Cercle
                            <ArrowUpRight className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' strokeWidth={1.5} />
                        </a>
                    </div>

                    {FOOTER_COLUMNS.map((column) => (
                        <nav key={column.title} aria-label={column.title}>
                            <p className='text-[0.7rem] uppercase tracking-[0.28em] text-champagne/70'>
                                {column.title}
                            </p>

                            <ul className='mt-6 space-y-3'>
                                {column.links.map((link) => (
                                    <li key={link.label}>
                                        <a
                                            href={link.href}
                                            className='text-sm text-ivory/70 transition-colors hover:text-ivory focus-visible:text-ivory focus-visible:outline-none'
                                        >
                                            {link.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    ))}

                    <div>
                        <p className='text-[0.7rem] uppercase tracking-[0.28em] text-champagne/70'>
                            The ateliers
                        </p>

                        <address className='mt-6 space-y-4 text-sm not-italic leading-relaxed text-ivory/70'>
                            <p>
                                <span className='block text-ivory'>Paris</span>
                                Faubourg Saint-Honoré, 8th arrondissement
                            </p>

                            <p>
                                Visits by appointment,
                                <br />
                                Tuesday to Saturday, 10am to 7pm
                            </p>
                        </address>
                    </div>
                </div>

                <p
                    aria-hidden='true'
                    className='mt-20 select-none bg-linear-to-b from-ivory/15 to-ivory/0 bg-clip-text text-center font-heading text-[clamp(4.5rem,17vw,15rem)] font-light leading-none tracking-[0.18em] text-transparent'
                >
                    AVELINE
                </p>

                <div className='mt-6 flex flex-col gap-3 border-t border-ivory/10 pt-8 text-xs text-ivory/50 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © <span suppressHydrationWarning>{new Date().getFullYear()}</span> Maison Aveline. All rights reserved.
                    </p>

                    <p className='uppercase tracking-[0.24em]'>
                        Made by hand in Paris
                    </p>
                </div>
            </div>
        </footer>
    )
}
